import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Home, LayoutGrid, List, BarChart, Tag, Layers } from 'lucide-react';
import { CustomBreadcrumb, CustomInput, CustomButton, CustomSelect } from '@/shared/components/atoms';
import { CustomCommonScreenHeader } from '@/shared/components/molecules';
import { useToastStore } from '@/shared/stores/toastStore';
import { useAuthStore } from '@/shared/stores/authStore';

const addLevelSchema = z.object({
    name: z.string(), // Auto-generated
    role: z.string().min(2, 'Role description is required'),
    parent_id: z.string().optional(),
});

type AddLevelFormData = z.infer<typeof addLevelSchema>;

export const AddDepartmentLevel = () => {
    const navigate = useNavigate();
    const { departmentId, typeId } = useParams<{ departmentId: string, typeId: string }>();
    const [isLoading, setIsLoading] = useState(false);
    const { showToast } = useToastStore();
    const { user } = useAuthStore();
    const schoolSlug = user?.school?.slug || '';

    // Mock parent levels for select
    const parentLevels = [
        { label: 'None (Top Level)', value: 'none' },
        { label: 'L1 (Head / Director)', value: '1' },
        { label: 'L101 (Manager)', value: '2' },
        { label: 'L102 (Supervisor)', value: '3' },
        { label: 'L103 (Staff / Executive)', value: '4' },
    ];

    // Expanded Mock Data for logic calculation
    const existingLevels = [
        { id: '1', name: 'L1', parent_id: 'none' },
        { id: '2', name: 'L101', parent_id: '1' },
        { id: '3', name: 'L102', parent_id: '1' },
        { id: '4', name: 'L103', parent_id: '1' },
    ];

    const {
        register,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useForm<AddLevelFormData>({
        resolver: zodResolver(addLevelSchema)
    });

    const selectedParentId = watch('parent_id');

    React.useEffect(() => {
        if (!selectedParentId) return;

        if (selectedParentId === 'none') {
            const topLevels = existingLevels.filter(l => l.parent_id === 'none');
            const max = topLevels.length > 0 ? Math.max(...topLevels.map(l => parseInt(l.name.replace('L', '')) || 0)) : 0;
            setValue('name', `L${max + 1}`);
        } else {
            const parent = existingLevels.find(l => l.id === selectedParentId);
            if (parent) {
                const children = existingLevels.filter(l => l.parent_id === selectedParentId);
                if (children.length === 0) {
                    setValue('name', `${parent.name}01`);
                } else {
                    const maxChild = Math.max(...children.map(l => {
                        const suffix = l.name.replace(parent.name, '');
                        return parseInt(suffix) || 0;
                    }));
                    const nextSuffix = (maxChild + 1).toString().padStart(2, '0');
                    setValue('name', `${parent.name}${nextSuffix}`);
                }
            }
        }
    }, [selectedParentId, setValue]);

    const onSubmit = async (data: AddLevelFormData) => {
        setIsLoading(true);
        try {
            // Mock API call
            await new Promise(resolve => setTimeout(resolve, 1000));
            showToast('Level added successfully (Demo)', 'success');
            navigate(`/${schoolSlug}/school-admin/departments/${departmentId}/types/${typeId}/levels`);
        } catch (error) {
            showToast('Failed to add level', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={{ padding: '1rem 1.5rem' }}>
            <CustomBreadcrumb
                items={[
                    { label: 'Home', path: '/', icon: <Home size={14} /> },
                    { label: 'Departments', path: `/${schoolSlug}/school-admin/departments`, icon: <LayoutGrid size={14} /> },
                    { label: 'Types', path: `/${schoolSlug}/school-admin/departments/${departmentId}/types`, icon: <List size={14} /> },
                    { label: 'Levels', path: `/${schoolSlug}/school-admin/departments/${departmentId}/types/${typeId}/levels`, icon: <BarChart size={14} /> },
                    { label: 'Add Level' }
                ]}
            />

            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                <CustomCommonScreenHeader
                    title="Add Hierarchical Level"
                    subtitle="Create a new reporting level for this department type"
                    icon={<BarChart size={24} />}
                />
            </div>

            <div style={{
                maxWidth: '700px',
                margin: '2rem auto',
                background: 'var(--card-bg)',
                borderRadius: '16px',
                border: '1px solid var(--border-color)',
                padding: '2.5rem',
                boxShadow: 'var(--card-shadow)'
            }}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                        <div style={{ gridColumn: 'span 1' }}>
                            <CustomInput
                                label="Level Indicator (Auto-Generated)"
                                placeholder="Select a parent level first"
                                leftIcon={<Tag size={18} />}
                                error={errors.name?.message}
                                {...register('name')}
                                readOnly
                            />
                        </div>

                        <div style={{ gridColumn: 'span 1' }}>
                            <CustomInput
                                label="Role Description"
                                placeholder="Manager, Staff, etc."
                                leftIcon={<BarChart size={18} />}
                                error={errors.role?.message}
                                {...register('role')}
                                required
                            />
                        </div>

                        <div style={{ gridColumn: 'span 2' }}>
                            <CustomSelect
                                label="Reports To (Parent Level)"
                                options={parentLevels}
                                placeholder="Select parent level"
                                leftIcon={<Layers size={18} />}
                                error={errors.parent_id?.message}
                                {...register('parent_id')}
                            />
                        </div>

                        <div style={{
                            gridColumn: 'span 2',
                            marginTop: '2rem',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '1rem',
                            borderTop: '1px solid var(--border-color)',
                            paddingTop: '2rem'
                        }}>
                            <CustomButton
                                variant="outline"
                                color="secondary"
                                onClick={() => navigate(-1)}
                                disabled={isLoading}
                            >
                                Cancel
                            </CustomButton>
                            <CustomButton
                                type="submit"
                                variant="filled"
                                color="primary"
                                loading={isLoading}
                            >
                                Add Level
                            </CustomButton>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};
